import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Boxes, Cpu, Radio } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";
import { useBookCall } from "@/components/BookCallContext";
import SEO from "@/components/SEO";

/**
 * Technology page, 3 tabs at top, one diagram at a time, with the
 * "Build your own agent" configurator ALWAYS visible below.
 *
 * The three diagram sections and the configurator are inside
 * public/technology-embed.html. React posts a `kyroTechTab` message on tab
 * change; the embed toggles a body class and only the requested `.tab-sec`
 * stays visible. The `#configurator` section is not a `.tab-sec` so it
 * shows on every tab.
 */
const TABS = [
    { id: "stack", label: "AI Platform Architecture", icon: <Boxes size={16} /> },
    { id: "engine", label: "The Kyro Engine", icon: <Cpu size={16} /> },
    { id: "pipe", label: "Communication Pipeline", icon: <Radio size={16} /> },
];

export default function Technology() {
    const frameRef = useRef(null);
    const [height, setHeight] = useState(1400);
    const [tab, setTab] = useState("stack");
    const [loaded, setLoaded] = useState(false);
    const location = useLocation();
    const { openModal } = useBookCall();

    // Height sync + anchor resolution (for the "Generate my AI agent" deep-link).
    useEffect(() => {
        const onMsg = (e) => {
            if (!e.data) return;
            if (e.data.type === "kyroTechHeight") {
                const h = Number(e.data.height);
                if (h && !Number.isNaN(h)) setHeight(h);
            }
            if (e.data.type === "kyroTechAnchor") {
                const iframe = frameRef.current;
                if (!iframe) return;
                const y = iframe.getBoundingClientRect().top + window.scrollY + Number(e.data.offset || 0);
                window.scrollTo({ top: y - 16, behavior: "smooth" });
            }
            // Unify Book-a-Demo: iframe asks parent to open the site-wide modal.
            if (e.data.type === "kyroTechOpenBookDemo") {
                openModal({ source: "technology" });
            }
        };
        window.addEventListener("message", onMsg);
        return () => window.removeEventListener("message", onMsg);
    }, [openModal]);

    // Push the active tab to the iframe whenever it changes (and once loaded).
    useEffect(() => {
        const iframe = frameRef.current;
        if (!iframe?.contentWindow) return;
        iframe.contentWindow.postMessage({ type: "kyroTechTab", tab }, "*");
    }, [tab, loaded]);

    // Relay parent scroll to the iframe so the configurator's right-side
    // agent preview can stay pinned near the parent viewport top (real CSS
    // sticky doesn't work here because the iframe doesn't scroll, the parent
    // window does).
    useEffect(() => {
        const post = () => {
            const iframe = frameRef.current;
            if (!iframe?.contentWindow) return;
            const rect = iframe.getBoundingClientRect();
            const visibleTop = -rect.top; // iframe docY at parent viewport top
            iframe.contentWindow.postMessage(
                {
                    type: "parentScroll",
                    visibleTop,
                    vh: window.innerHeight,
                },
                "*"
            );
        };
        window.addEventListener("scroll", post, { passive: true });
        window.addEventListener("resize", post);
        post();
        return () => {
            window.removeEventListener("scroll", post);
            window.removeEventListener("resize", post);
        };
    }, [loaded]);

    // Handle #configurator deep-link
    useEffect(() => {
        if (!location.hash) return;
        const id = location.hash.replace("#", "");
        const attempt = (tries = 0) => {
            const iframe = frameRef.current;
            if (!iframe?.contentWindow) return tries < 20 && setTimeout(() => attempt(tries + 1), 150);
            iframe.contentWindow.postMessage({ type: "kyroTechAnchorRequest", id }, "*");
        };
        setTimeout(() => attempt(), 350);
    }, [location.hash, height]);

    return (
        <div data-testid={TESTIDS.tech.root}>
            <SEO
                path="/technology"
                title="Technology, the KyroLabs Engine, Communication Pipeline & Stack"
                description="Inside the KyroLabs technology stack: our agentic engine, the reasoning / voice / RAG communication pipeline, and the end-to-end tech stack that powers every enterprise AI agent we ship."
            />
            {/* Slim tab switcher, matches the detail-page pattern. The
                leading "Explore technology" label has been removed per
                feedback, only the pills remain. */}
            <div style={switcherBar}>
                <div className="wrap" style={switcherWrap}>
                    <div className="siblings-row" style={{ display: "flex", gap: 8, overflowX: "auto", flex: 1, minWidth: 0 }}>
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`tech-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                style={pillStyle(tab === t.id)}
                            >
                                <span
                                    style={{
                                        display: "inline-flex",
                                        color: tab === t.id ? "var(--accent)" : "var(--kl-muted)",
                                    }}
                                >
                                    {t.icon}
                                </span>
                                <span style={{ whiteSpace: "nowrap", fontSize: ".82rem" }}>{t.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Page hero from React removed, the embed's own tech-stack /
                engine / pipeline section header now sits directly under the
                switcher (see technology-embed.html `.hd` + `<h2>`). */}

            <iframe
                ref={frameRef}
                title="KyroLabs Technology"
                src="/technology-embed.html"
                onLoad={() => setLoaded((v) => !v)}
                scrolling="no"
                style={{
                    display: "block",
                    width: "100%",
                    height,
                    border: "0",
                    background: "#0a0d12",
                }}
            />
        </div>
    );
}

/* ---------- styles (reused from EntityDetailPage) ---------- */

const switcherBar = {
    borderBottom: "1px solid var(--line)",
    background: "rgba(255,255,255,0.015)",
    padding: "10px 0",
};
const switcherWrap = { display: "flex", alignItems: "center", gap: 16 };
const pillStyle = (isActive) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 12px",
    borderRadius: 999,
    border: isActive ? "1px solid var(--accent)" : "1px solid var(--line)",
    background: isActive ? "rgba(143,208,255,0.10)" : "transparent",
    color: isActive ? "var(--ink)" : "var(--kl-muted)",
    fontFamily: "inherit",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all .16s",
    boxShadow: isActive ? "0 0 0 3px rgba(143,208,255,0.16)" : "none",
    flexShrink: 0,
});
